import { prettyDateTime } from "@app/date";
import {
  deleteLlmTraceDrain,
  deprovisionLogDrain,
  deprovisionMetricDrain,
  fetchAccountLlmTraceDrains,
  fetchEnvLogDrains,
  fetchEnvMetricDrains,
  fetchLlmGatewayConfiguration,
  fetchLlmPolicy,
  isLogDrainHttps,
  isLogDrainSyslog,
  restartLogDrain,
  selectDatabaseById,
  selectLlmGatewayConfiguration,
  selectLlmTraceDrainsByEnvId,
  selectLogDrainsByEnvId,
  selectMetricDrainsByEnvId,
} from "@app/deploy";
import { selectOrganizationSelectedId } from "@app/organizations";
import {
  useDispatch,
  useLoader,
  useLoaderSuccess,
  useQuery,
  useSelector,
} from "@app/react";
import {
  createLlmTraceDrainUrl,
  createLogDrainUrl,
  createMetricDrainUrl,
  databaseDetailUrl,
  operationDetailUrl,
} from "@app/routes";
import { capitalize } from "@app/string-utils";
import type {
  DeployLlmTraceDrain,
  DeployLogDrain,
  DeployMetricDrain,
} from "@app/types";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import { usePaginate } from "../hooks";
import {
  ButtonDestroy,
  ButtonOps,
  Code,
  DescBar,
  EmptyTr,
  FilterBar,
  Group,
  IconPlusCircle,
  PaginateBar,
  Pill,
  type PillVariant,
  SensitiveLogDrainField,
  TBody,
  THead,
  Table,
  Td,
  Th,
  Tooltip,
  Tr,
  tokens,
} from "../shared";

const DrainStatusPill = ({
  drain,
}: {
  drain: DeployLogDrain | DeployMetricDrain;
}) => {
  let pillVariant: PillVariant = "progress";
  if (
    drain.status === "pending" ||
    drain.status === "provisioning" ||
    drain.status === "deprovisioning"
  ) {
    pillVariant = "pending";
  }
  if (drain.status === "provisioned") {
    pillVariant = "success";
  }
  if (
    drain.status === "deprovision_failed" ||
    drain.status === "provision_failed"
  ) {
    pillVariant = "error";
  }

  let drainStatus = drain.status;
  if (drain.status === "deprovisioning" && drain.deletedAt) {
    pillVariant = "error";
    drainStatus = "deprovisioned";
  }

  return <Pill variant={pillVariant}>{capitalize(drainStatus)}</Pill>;
};

const LogDrainPrimaryCell = ({ logDrain }: { logDrain: DeployLogDrain }) => {
  return (
    <Td className="flex-1">
      <div className="flex">
        <DrainStatusPill drain={logDrain} />
      </div>
    </Td>
  );
};

const LogDrainHandleCell = ({ logDrain }: { logDrain: DeployLogDrain }) => {
  return (
    <Td className="flex-1">
      <div className="flex">
        <p className="leading-4">
          <span className={tokens.type.darker}>{logDrain.handle}</span>
          <br />
          <span className={tokens.type["small lighter"]}>
            {capitalize(logDrain.drainType.replace("_", " "))}
          </span>
        </p>
      </div>
    </Td>
  );
};

const LogDrainSourcesCell = ({ logDrain }: { logDrain: DeployLogDrain }) => {
  const drainSources = [];
  if (logDrain.drainApps) {
    drainSources.push("Apps");
  }
  if (logDrain.drainDatabases) {
    drainSources.push("Databases");
  }
  if (logDrain.drainEphemeralSessions) {
    drainSources.push("SSH Sessions");
  }
  if (logDrain.drainProxies) {
    drainSources.push("Proxies");
  }

  return (
    <Td className="flex-1">
      <div className="flex">
        <p className="leading-4">
          <span className={tokens.type.darker}>
            {drainSources.length > 0 ? drainSources.join(", ") : "N/A"}
          </span>
        </p>
      </div>
    </Td>
  );
};

const LogDrainLastUpdatedCell = ({
  logDrain,
}: { logDrain: DeployLogDrain }) => {
  return (
    <Td className="flex-1">
      <div className="flex">
        <p className="leading-4">
          <span className={tokens.type.darker}>
            {capitalize(prettyDateTime(logDrain.updatedAt))}
          </span>
        </p>
      </div>
    </Td>
  );
};

const LogDrainActions = ({ logDrain }: { logDrain: DeployLogDrain }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const restartAction = restartLogDrain({ id: logDrain.id });
  const restartLoader = useLoader(restartAction);
  const submitRestart = () => {
    dispatch(restartAction);
  };
  useLoaderSuccess(restartLoader, () => {
    navigate(operationDetailUrl(restartLoader.meta.opId));
  });

  const deprovisionAction = deprovisionLogDrain({ id: logDrain.id });
  const deprovisionLoader = useLoader(deprovisionAction);
  const submitDeprovision = () => {
    dispatch(deprovisionAction);
  };
  useLoaderSuccess(deprovisionLoader, () => {
    navigate(operationDetailUrl(deprovisionLoader.meta.opId));
  });

  return (
    <Td className="flex justify-end gap-2 mr-4">
      <Group variant="horizontal" size="sm">
        {logDrain.drainType === "tail" ? (
          <ButtonOps
            size="sm"
            envId={logDrain.environmentId}
            className="semibold"
            onClick={submitRestart}
            isLoading={restartLoader.isLoading}
            disabled={logDrain.backendChannel === "log_forwarder"}
          >
            Restart
          </ButtonOps>
        ) : null}

        <ButtonDestroy
          size="sm"
          envId={logDrain.environmentId}
          className="semibold"
          onClick={submitDeprovision}
          isLoading={deprovisionLoader.isLoading}
          variant="delete"
          requireConfirm
        >
          Delete
        </ButtonDestroy>
      </Group>
    </Td>
  );
};

const LogDrainDestinationCell = ({
  logDrain,
}: { logDrain: DeployLogDrain }) => {
  const database = useSelector((s) =>
    selectDatabaseById(s, { id: logDrain.databaseId }),
  );
  if (logDrain.drainType === "tail") {
    return (
      <Tooltip text="This log drain was automatically provisioned to support `aptible logs` in this environment. Access these logs via the Aptible CLI.">
        <Code>Tail</Code>
      </Tooltip>
    );
  }

  if (logDrain.drainType === "elasticsearch_database") {
    return (
      <>
        <Link to={databaseDetailUrl(database.id)}>{database.handle}</Link>
        {logDrain.loggingToken ? (
          <SensitiveLogDrainField
            logDrainId={logDrain.id}
            envId={logDrain.environmentId}
            fieldValue={logDrain.loggingToken}
            label="Pipeline"
          />
        ) : null}
      </>
    );
  }

  if (logDrain.drainType === "datadog") {
    return (
      <SensitiveLogDrainField
        logDrainId={logDrain.id}
        envId={logDrain.environmentId}
        fieldValue={logDrain.drainUsername}
        label="Datadog"
        renderValue={(drainUsername) => {
          const url =
            logDrain.url ||
            `https://http-intake.logs.datadoghq.com/v1/input/${drainUsername}`;

          const tags = (logDrain.loggingToken ?? "")
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean);

          return (
            <Tooltip
              fluid
              text={
                <>
                  <strong>URL:</strong> <code className="text-xs">{url}</code>
                  {tags.length > 0 && (
                    <>
                      <br />
                      <strong>Tags:</strong>{" "}
                      <code className="text-xs">{tags.join(", ")}</code>
                    </>
                  )}
                </>
              }
            >
              <Code>{url}</Code>
            </Tooltip>
          );
        }}
      />
    );
  }

  if (isLogDrainHttps(logDrain)) {
    return (
      <SensitiveLogDrainField
        logDrainId={logDrain.id}
        envId={logDrain.environmentId}
        fieldValue={logDrain.url}
        label="URL"
      />
    );
  }

  if (isLogDrainSyslog(logDrain)) {
    return (
      <Group variant="horizontal" size="sm" className="items-center">
        <Code>
          {logDrain.drainHost}:{logDrain.drainPort}
        </Code>
        {logDrain.loggingToken ? (
          <SensitiveLogDrainField
            logDrainId={logDrain.id}
            envId={logDrain.environmentId}
            fieldValue={logDrain.loggingToken}
            label="Token"
          />
        ) : null}
      </Group>
    );
  }

  return null;
};

const LogDrainTable = ({
  envId,
  onCreate,
}: {
  envId: string;
  onCreate: () => void;
}) => {
  useQuery(fetchEnvLogDrains({ id: envId }));
  const logDrains = useSelector((s) => selectLogDrainsByEnvId(s, { envId }));
  const paginated = usePaginate(logDrains);

  return (
    <Group>
      <div className="flex justify-end">
        <ButtonOps envId={envId} onClick={onCreate}>
          <IconPlusCircle variant="sm" className="mr-1" /> New Log Drain
        </ButtonOps>
      </div>
      <Group size="sm">
        <FilterBar>
          <Group variant="horizontal" size="lg" className="items-center">
            <DescBar>{paginated.totalItems} Log Drains</DescBar>
            <PaginateBar {...paginated} />
          </Group>
        </FilterBar>
      </Group>

      <Table>
        <THead>
          <Th>Status</Th>
          <Th>Handle</Th>
          <Th>Destination</Th>
          <Th>Sources</Th>
          <Th>Last Updated</Th>
          <Th variant="right">Actions</Th>
        </THead>

        <TBody>
          {paginated.data.length === 0 ? <EmptyTr colSpan={6} /> : null}
          {paginated.data.map((drain) => (
            <Tr key={drain.id}>
              <LogDrainPrimaryCell logDrain={drain} />
              <LogDrainHandleCell logDrain={drain} />
              <Td className="flex items-center h-full min-w-[280px]">
                <LogDrainDestinationCell logDrain={drain} />
              </Td>
              <LogDrainSourcesCell logDrain={drain} />
              <LogDrainLastUpdatedCell logDrain={drain} />
              <LogDrainActions logDrain={drain} />
            </Tr>
          ))}
        </TBody>
      </Table>
    </Group>
  );
};

const MetricDrainPrimaryCell = ({
  metricDrain,
}: { metricDrain: DeployMetricDrain }) => {
  return (
    <Td className="flex-1">
      <div className="flex">
        <DrainStatusPill drain={metricDrain} />
      </div>
    </Td>
  );
};

const MetricDrainHandleCell = ({
  metricDrain,
}: { metricDrain: DeployMetricDrain }) => {
  return (
    <Td className="flex-1">
      <div className="flex">
        <p className="leading-4">
          <span className={tokens.type.darker}>{metricDrain.handle}</span>
          <br />
          <span className={tokens.type["small lighter"]}>
            {capitalize(metricDrain.drainType.replace("_", " "))}
          </span>
        </p>
      </div>
    </Td>
  );
};

const MetricDrainDatabaseCell = ({
  metricDrain,
}: { metricDrain: DeployMetricDrain }) => {
  const database = useSelector((s) =>
    selectDatabaseById(s, { id: metricDrain.databaseId }),
  );
  return (
    <Td className="flex-1">
      <Link to={databaseDetailUrl(database.id)}>{database.handle}</Link>
    </Td>
  );
};

const MetricDrainLastUpdatedCell = ({
  metricDrain,
}: { metricDrain: DeployMetricDrain }) => {
  return (
    <Td className="flex-1">
      <div className="flex">
        <p className="leading-4">
          <span className={tokens.type.darker}>
            {prettyDateTime(metricDrain.updatedAt)}
          </span>
        </p>
      </div>
    </Td>
  );
};

const MetricDrainActions = ({
  metricDrain,
}: { metricDrain: DeployMetricDrain }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const deprovisionAction = deprovisionMetricDrain({ id: metricDrain.id });
  const deprovisionLoader = useLoader(deprovisionAction);
  const submitDeprovision = () => {
    dispatch(deprovisionAction);
  };
  useLoaderSuccess(deprovisionLoader, () => {
    navigate(operationDetailUrl(deprovisionLoader.meta.opId));
  });

  return (
    <Td className="flex justify-end gap-2 mr-4">
      <Group variant="horizontal" size="sm">
        <ButtonDestroy
          size="sm"
          envId={metricDrain.environmentId}
          className="semibold"
          onClick={submitDeprovision}
          isLoading={deprovisionLoader.isLoading}
          variant="delete"
          requireConfirm
        >
          Delete
        </ButtonDestroy>
      </Group>
    </Td>
  );
};

const MetricDrainTable = ({
  envId,
  onCreate,
}: {
  envId: string;
  onCreate: () => void;
}) => {
  useQuery(fetchEnvMetricDrains({ id: envId }));
  const metricDrains = useSelector((s) =>
    selectMetricDrainsByEnvId(s, { envId }),
  );
  const paginated = usePaginate(metricDrains);

  return (
    <Group>
      <div className="flex justify-end">
        <ButtonOps envId={envId} onClick={onCreate}>
          <IconPlusCircle variant="sm" className="mr-1" /> New Metric Drain
        </ButtonOps>
      </div>
      <Group size="sm">
        <FilterBar>
          <Group variant="horizontal" size="lg" className="items-center">
            <DescBar>{paginated.totalItems} Metric Drains</DescBar>
            <PaginateBar {...paginated} />
          </Group>
        </FilterBar>
      </Group>

      <Table>
        <THead>
          <Th>Status</Th>
          <Th>Handle</Th>
          <Th>Database</Th>
          <Th>Last Updated</Th>
          <Th variant="right">Actions</Th>
        </THead>

        <TBody>
          {paginated.data.length === 0 ? <EmptyTr colSpan={5} /> : null}
          {paginated.data.map((drain) => (
            <Tr key={drain.id}>
              <MetricDrainPrimaryCell metricDrain={drain} />
              <MetricDrainHandleCell metricDrain={drain} />
              <MetricDrainDatabaseCell metricDrain={drain} />
              <MetricDrainLastUpdatedCell metricDrain={drain} />
              <MetricDrainActions metricDrain={drain} />
            </Tr>
          ))}
        </TBody>
      </Table>
    </Group>
  );
};

const LlmTraceDrainActions = ({
  drain,
  envId,
  orgId,
}: {
  drain: DeployLlmTraceDrain;
  envId: string;
  orgId: string;
}) => {
  const dispatch = useDispatch();
  const deleteAction = deleteLlmTraceDrain({
    drainId: drain.id,
    orgId,
    accountId: envId,
  });
  const deleteLoader = useLoader(deleteAction);
  const submitDelete = () => {
    dispatch(deleteAction);
  };

  return (
    <Td className="flex justify-end gap-2 mr-4">
      <Group variant="horizontal" size="sm">
        <ButtonDestroy
          size="sm"
          envId={envId}
          className="semibold"
          onClick={submitDelete}
          isLoading={deleteLoader.isLoading}
          variant="delete"
          requireConfirm
        >
          Delete
        </ButtonDestroy>
      </Group>
    </Td>
  );
};

const LlmTraceDrainSection = ({
  envId,
  onCreate,
}: {
  envId: string;
  onCreate: () => void;
}) => {
  const orgId = useSelector(selectOrganizationSelectedId);
  useQuery(fetchLlmPolicy({ envId, orgId }));
  useQuery(fetchAccountLlmTraceDrains({ id: envId, orgId }));

  const drains = useSelector((s) => selectLlmTraceDrainsByEnvId(s, { envId }));
  const paginated = usePaginate(drains);

  return (
    <div id="llm-trace-drains" className="flex flex-col gap-4">
      <div className="flex justify-end">
        <ButtonOps envId={envId} onClick={onCreate}>
          <IconPlusCircle variant="sm" className="mr-1" /> New LLM Trace Drain
        </ButtonOps>
      </div>
      <Group size="sm">
        <FilterBar>
          <Group variant="horizontal" size="lg" className="items-center">
            <DescBar>{paginated.totalItems} LLM Trace Drains</DescBar>
            <PaginateBar {...paginated} />
          </Group>
        </FilterBar>
      </Group>

      <Table>
        <THead>
          <Th>Handle</Th>
          <Th>Destination</Th>
          <Th>Last Updated</Th>
          <Th variant="right">Actions</Th>
        </THead>

        <TBody>
          {paginated.data.length === 0 ? <EmptyTr colSpan={4} /> : null}
          {paginated.data.map((drain) => (
            <Tr key={drain.id}>
              <Td className="flex-1">
                <div className="flex">
                  <p className="leading-4">
                    <span className={tokens.type.darker}>{drain.handle}</span>
                  </p>
                </div>
              </Td>
              <Td className="flex-1 min-w-[280px]">
                <Tooltip fluid text={drain.url}>
                  <Code className="truncate max-w-[360px] block">
                    {drain.url}
                  </Code>
                </Tooltip>
              </Td>
              <Td className="flex-1">
                <span className={tokens.type.darker}>
                  {capitalize(prettyDateTime(drain.updatedAt))}
                </span>
              </Td>
              <LlmTraceDrainActions drain={drain} envId={envId} orgId={orgId} />
            </Tr>
          ))}
        </TBody>
      </Table>
    </div>
  );
};

export const EnvironmentIntegrationsPage = () => {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const orgId = useSelector(selectOrganizationSelectedId);
  useQuery(fetchLlmGatewayConfiguration({ orgId }));
  const gatewayConfig = useSelector(selectLlmGatewayConfiguration);
  const showLlmTraceDrains = !!gatewayConfig?.id;

  const onCreateMetrics = () => {
    navigate(createMetricDrainUrl(id));
  };
  const onCreateLogs = () => {
    navigate(createLogDrainUrl(id));
  };
  const onCreateLlmTraceDrain = () => {
    navigate(createLlmTraceDrainUrl(id));
  };

  return (
    <Group>
      <LogDrainTable envId={id} onCreate={onCreateLogs} />
      <MetricDrainTable envId={id} onCreate={onCreateMetrics} />
      {showLlmTraceDrains ? (
        <LlmTraceDrainSection envId={id} onCreate={onCreateLlmTraceDrain} />
      ) : null}
    </Group>
  );
};
